import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaPlus, FaLink, FaEye, FaEdit, FaTrash, FaCopy, FaUsers, FaChartLine, FaCheck, FaSearch, FaSync } from 'react-icons/fa';
import ReferralService from '../../../service/admin/referralService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { useLoader } from '../../../base/context/LoaderProvider';
import ExcelTable from '../../../component/library/excelTable';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import './style.css';

const REFERRAL_TYPES = [
  { value: 'influencer', label: 'Influencer' },
  { value: 'partner', label: 'Partner' },
  { value: 'affiliate', label: 'Affiliate' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'social', label: 'Social Media' },
  { value: 'other', label: 'Other' },
];

const isSuccess = (res) => res?.status?.code === 200;
const getData = (res) => res?.data;
const getMessage = (res) => res?.status?.message || res?.error?.message;

export default function ReferralManagement() {
  const message = useMessage();
  const loader = useLoader();

  const [dashboard, setDashboard] = useState(null);
  const [codes, setCodes] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedCodes, setSelectedCodes] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSummary, setDeleteSummary] = useState({ visits: 0, signups: 0, commissionPercent: 0, conversionRate: '0' });
  const [copiedId, setCopiedId] = useState(null);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'other',
    commissionPercent: 0,
    expiresAt: '',
  });

  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await ReferralService.getDashboard();
      if (isSuccess(res)) setDashboard(getData(res));
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  }, []);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: pagination.limit };
      if (searchQuery) params.search = searchQuery;
      if (filterType) params.type = filterType;

      const res = await ReferralService.getCodes(params);
      if (isSuccess(res)) {
        const data = getData(res);
        setCodes(data.codes || []);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (err) {
      message.showToast('Failed to fetch referral codes', ToastTypes.ERROR);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [pagination.page, pagination.limit, searchQuery, filterType, message]);

  useEffect(() => {
    fetchDashboard();
    fetchCodes();
  }, [fetchDashboard, fetchCodes]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.code || !formData.name) {
      message.showToast('Code and name are required', ToastTypes.ERROR);
      return;
    }
    loader.showLoading(true);
    try {
      const res = await ReferralService.createCode(formData);
      if (isSuccess(res)) {
        message.showToast('Referral code created', ToastTypes.SUCCESS);
        setShowCreateModal(false);
        resetForm();
        fetchCodes();
        fetchDashboard();
      } else {
        message.showToast(getMessage(res) || 'Failed to create', ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || 'Failed to create referral code', ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedCode) return;
    loader.showLoading(true);
    try {
      const res = await ReferralService.updateCode(selectedCode._id, formData);
      if (isSuccess(res)) {
        message.showToast('Referral code updated', ToastTypes.SUCCESS);
        setShowEditModal(false);
        setSelectedCode(null);
        resetForm();
        fetchCodes();
      } else {
        message.showToast(getMessage(res) || 'Failed to update', ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || 'Failed to update referral code', ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    loader.showLoading(true);
    try {
      const res = await ReferralService.deleteCode(deleteTarget._id);
      if (isSuccess(res)) {
        message.showToast('Referral code deleted', ToastTypes.SUCCESS);
        setShowDeleteModal(false);
        setDeleteTarget(null);
        fetchCodes();
        fetchDashboard();
      } else {
        message.showToast(getMessage(res) || 'Failed to delete', ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || 'Failed to delete referral code', ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  };

  const handleCopyUrl = async (code, e) => {
    e?.stopPropagation();
    try {
      const res = await ReferralService.generateUrl(code._id, 'hash');
      if (isSuccess(res)) {
        await navigator.clipboard.writeText(getData(res).url);
        setCopiedId(code._id);
        setTimeout(() => setCopiedId(null), 2000);
        message.showToast('URL copied to clipboard', ToastTypes.SUCCESS);
      }
    } catch (err) {
      message.showToast('Failed to copy URL', ToastTypes.ERROR);
    }
  };

  const openDetailPage = (code) => {
    window.open(`/admin/referral-management/${code._id}`, '_blank');
  };

  const handleEdit = (code, e) => {
    e?.stopPropagation();
    setSelectedCode(code);
    setFormData({
      code: code.code,
      name: code.name,
      description: code.description || '',
      type: code.type || 'other',
      commissionPercent: code.commissionPercent || 0,
      expiresAt: code.expiresAt ? code.expiresAt.split('T')[0] : '',
      isActive: code.isActive,
    });
    setShowEditModal(true);
  };

  const calcConversionRate = (visitsCount, signupsCount) => {
    if (!visitsCount) return '0';
    return ((signupsCount / visitsCount) * 100).toFixed(1);
  };

  const openDeleteConfirm = (code, e) => {
    e?.stopPropagation();
    const visitsCount = Number(code?.stats?.totalVisits || 0);
    const signupsCount = Number(code?.stats?.signups || 0);
    const commissionPercent = Number(code?.commissionPercent || 0);
    setDeleteTarget(code);
    setDeleteSummary({
      visits: visitsCount,
      signups: signupsCount,
      commissionPercent,
      conversionRate: calcConversionRate(visitsCount, signupsCount),
    });
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setFormData({ code: '', name: '', description: '', type: 'other', commissionPercent: 0, expiresAt: '' });
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleSelectionChange = (selected) => {
    setSelectedCodes(selected);
  };

  const tableData = useMemo(() => 
    (codes || []).map((item, index) => ({
      ...item,
      serialNumber: (pagination.page - 1) * pagination.limit + index + 1,
    })),
    [codes, pagination.page, pagination.limit]
  );

  const columns = [
    {
      key: "serialNumber",
      label: "S.No.",
      width: 60,
      render: (value) => <span className="rm-serial">{value}</span>,
    },
    {
      key: "code",
      label: "Code",
      width: 140,
      render: (value, row) => (
        <span 
          className="rm-code-badge" 
          onClick={(e) => { e.stopPropagation(); openDetailPage(row); }}
          title="View Details"
        >
          {value}
        </span>
      ),
    },
    {
      key: "name",
      label: "Name",
      width: 180,
      render: (value) => <span className="rm-name">{value || "-"}</span>,
    },
    {
      key: "type",
      label: "Type",
      width: 100,
      render: (value) => (
        <span className={`rm-type-badge rm-type-${value || 'other'}`}>
          {value || 'other'}
        </span>
      ),
    },
    {
      key: "stats",
      label: "Visits",
      width: 80,
      render: (value) => <span className="rm-center">{value?.totalVisits || 0}</span>,
    },
    {
      key: "signups",
      label: "Signups",
      width: 80,
      render: (_, row) => <span className="rm-center">{row.stats?.signups || 0}</span>,
    },
    {
      key: "commissionPercent",
      label: "Commission %",
      width: 100,
      render: (value) => <span className="rm-center">{Number(value || 0)}%</span>,
    },
    {
      key: "isActive",
      label: "Status",
      width: 90,
      render: (value) => (
        <span className={`rm-status-badge ${value ? 'rm-status-active' : 'rm-status-inactive'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      width: 110,
      render: (value) => formatDate(value),
    },
    {
      key: "actions",
      label: "Actions",
      width: 140,
      render: (_, row) => (
        <div className="rm-actions-cell">
          <button className="rm-action-btn rm-action-copy" onClick={(e) => handleCopyUrl(row, e)} title="Copy URL">
            {copiedId === row._id ? <FaCheck /> : <FaCopy />}
          </button>
          <button className="rm-action-btn rm-action-view" onClick={(e) => { e.stopPropagation(); openDetailPage(row); }} title="View Details">
            <FaEye />
          </button>
          <button className="rm-action-btn rm-action-users" onClick={(e) => { e.stopPropagation(); openDetailPage(row); }} title="View Users">
            <FaUsers />
          </button>
          <button className="rm-action-btn rm-action-edit" onClick={(e) => handleEdit(row, e)} title="Edit">
            <FaEdit />
          </button>
          <button className="rm-action-btn rm-action-delete" onClick={(e) => openDeleteConfirm(row, e)} title="Delete">
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  const startItem = (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(pagination.page * pagination.limit, pagination.total);

  const toolbarLeftActions = (
    <div className="rm-toolbar-left">
      <div className="rm-search-box">
        <FaSearch className="rm-search-icon" />
        <input
          type="text"
          className="rm-search-input"
          placeholder="Search by code or name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyUp={(e) => e.key === "Enter" && fetchCodes()}
        />
      </div>
      <select className="rm-filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
        <option value="">All Types</option>
        {REFERRAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <button className="rm-search-btn" onClick={fetchCodes}>
        <FaSearch /> Search
      </button>
    </div>
  );

  const toolbarRightActions = (
    <div className="rm-toolbar-right">
      <button className="rm-refresh-btn" onClick={fetchCodes} title="Refresh">
        <FaSync className={loading ? "rm-spin" : ""} />
      </button>
      <button className="rm-create-btn" onClick={() => setShowCreateModal(true)}>
        <FaPlus /> Create Referral Code
      </button>
    </div>
  );

  return (
    <div className="rm-page">
      {/* Dashboard Stats */}
      {dashboard && (
        <div className="rm-stats-grid">
          <div className="rm-stat-card">
            <div className="rm-stat-icon rm-stat-blue"><FaLink /></div>
            <div className="rm-stat-content">
              <span className="rm-stat-value">{dashboard.overview.totalCodes}</span>
              <span className="rm-stat-label">Total Codes</span>
            </div>
          </div>
          <div className="rm-stat-card">
            <div className="rm-stat-icon rm-stat-green"><FaChartLine /></div>
            <div className="rm-stat-content">
              <span className="rm-stat-value">{dashboard.overview.totalVisits}</span>
              <span className="rm-stat-label">Total Visits</span>
            </div>
          </div>
          <div className="rm-stat-card">
            <div className="rm-stat-icon rm-stat-purple"><FaUsers /></div>
            <div className="rm-stat-content">
              <span className="rm-stat-value">{dashboard.overview.totalSignups}</span>
              <span className="rm-stat-label">Total Signups</span>
            </div>
          </div>
          <div className="rm-stat-card">
            <div className="rm-stat-icon rm-stat-orange"><FaChartLine /></div>
            <div className="rm-stat-content">
              <span className="rm-stat-value">{dashboard.overview.overallConversionRate}%</span>
              <span className="rm-stat-label">Conversion Rate</span>
            </div>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="rm-table-section">
        <ExcelTable
          data={tableData}
          columns={columns}
          isLoading={initialLoading || loading}
          loadingText="Loading referral codes..."
          showToolbarExport={true}
          showToolbarFilter={false}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarLeftActions={toolbarLeftActions}
          toolbarRightActions={toolbarRightActions}
          onSelectionChange={handleSelectionChange}
          selectedData={selectedCodes}
          selectable={false}
          sortable={true}
          resizable={true}
          showToolbar={true}
          title="Referral-Codes"
          onRowClick={openDetailPage}
          userId={currentUserId}
        />

        {codes.length > 0 && pagination.totalPages > 1 && (
          <div className="rm-pagination">
            <div className="rm-pagination-info">
              Showing {startItem} to {endItem} of {pagination.total} codes
            </div>
            <div className="rm-pagination-controls">
              <button className="rm-page-btn" disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>‹</button>
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) pageNum = i + 1;
                else if (pagination.page <= 3) pageNum = i + 1;
                else if (pagination.page >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                else pageNum = pagination.page - 2 + i;
                return (
                  <button
                    key={pageNum}
                    className={`rm-page-btn ${pagination.page === pageNum ? "rm-page-btn-active" : ""}`}
                    onClick={() => setPagination(p => ({ ...p, page: pageNum }))}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                <>
                  <span className="rm-page-dots">...</span>
                  <button className="rm-page-btn" onClick={() => setPagination(p => ({ ...p, page: p.totalPages }))}>{pagination.totalPages}</button>
                </>
              )}
              <button className="rm-page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="rm-modal-content" onClick={e => e.stopPropagation()}>
            <h2>Create Referral Code</h2>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Code *</label>
                <input type="text" value={formData.code} onChange={e => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '') })} placeholder="e.g., thefounderdream" required />
                <small>Only lowercase letters, numbers, hyphens, underscores</small>
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., The Founder Dream" required />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Notes about this referral source..." />
              </div>
              <div className="referral-form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                    {REFERRAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Commission %</label>
                  <input type="number" min="0" max="100" value={formData.commissionPercent} onChange={e => setFormData({ ...formData, commissionPercent: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="form-group">
                <label>Expires At (optional)</label>
                <input type="date" value={formData.expiresAt} onChange={e => setFormData({ ...formData, expiresAt: e.target.value })} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCode && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="rm-modal-content" onClick={e => e.stopPropagation()}>
            <h2>Edit Referral Code</h2>
            <form onSubmit={handleUpdate}>
              <div className="form-group">
                <label>Code</label>
                <input type="text" value={formData.code} disabled className="disabled" />
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="referral-form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                    {REFERRAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={formData.isActive ? 'true' : 'false'} onChange={e => setFormData({ ...formData, isActive: e.target.value === 'true' })}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="referral-form-row">
                <div className="form-group">
                  <label>Commission %</label>
                  <input type="number" min="0" max="100" value={formData.commissionPercent} onChange={e => setFormData({ ...formData, commissionPercent: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="form-group">
                  <label>Expires At</label>
                  <input type="date" value={formData.expiresAt} onChange={e => setFormData({ ...formData, expiresAt: e.target.value })} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Update</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="rm-modal-content" onClick={e => e.stopPropagation()}>
            <h2>Confirm Delete</h2>
            <div className="delete-summary">
              <div className="delete-summary-title">You are about to delete referral code: <strong>{deleteTarget.code}</strong></div>
              <div className="delete-summary-grid">
                <div className="delete-summary-item"><span className="label">Total Visits</span><span className="value">{deleteSummary.visits}</span></div>
                <div className="delete-summary-item"><span className="label">Total Signups</span><span className="value">{deleteSummary.signups}</span></div>
                <div className="delete-summary-item"><span className="label">Commission %</span><span className="value">{deleteSummary.commissionPercent}%</span></div>
                <div className="delete-summary-item"><span className="label">Conversion %</span><span className="value">{deleteSummary.conversionRate}%</span></div>
              </div>
              <div className="delete-summary-warning">This action cannot be undone.</div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button type="button" className="btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
